import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProviderAccess } from "@/hooks/useProviderAccess";
import { useUrlParams } from "@/hooks/useUrlParams";
import { useFavorites } from "@/hooks/useFavorites";
import { useProvider, useProviders } from "@/hooks/useProviders";
import { useProviderMinPrice } from "@/hooks/useProviderMinPrice";
import { useDomainProvider } from "@/hooks/useDomainProvider";
import AuthForm from "@/components/auth/AuthForm";
import ProviderImagesFromFacilities from "@/components/ProviderImagesFromFacilities";
import { Star, MapPin } from "lucide-react";

const ProviderCard = ({ provider }: { provider: any }) => {
  const navigate = useNavigate();
  const { data: minPrice } = useProviderMinPrice(provider.id);

  const getProviderFallbackImages = (): string[] => {
    if (Array.isArray(provider.images) && provider.images.length > 0) {
      return provider.images;
    } else if (provider.image_url) {
      return [provider.image_url];
    }
    return [];
  };

  return (
    <ProviderImagesFromFacilities
      providerId={provider.id}
      fallbackImages={getProviderFallbackImages()}
    >
      {(images) => (
        <div 
          className="group cursor-pointer"
          onClick={() => navigate(`/provider/${provider.slug || provider.id}`)}
        >
          <div className="relative aspect-[4/3] overflow-hidden rounded-xl mb-3">
            <img 
              src={images[0] || '/placeholder.svg'} 
              alt={provider.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
          
          <div className="space-y-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-medium text-foreground group-hover:text-foreground/70 transition-colors">
                {provider.name}
              </h3>
              <div className="flex items-center gap-1 text-sm shrink-0">
                <Star className="h-3.5 w-3.5 fill-foreground text-foreground" />
                <span className="text-sm">{provider.rating || '5'}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-1 text-muted-foreground text-sm">
              <MapPin className="h-3 w-3" />
              <span>{provider.location}</span>
            </div>
            
            <div className="text-sm">
              <span className="font-medium">
                {minPrice ? `From £${Number(minPrice).toFixed(0)}` : 'From £25'}
              </span>
            </div>
          </div>
        </div>
      )}
    </ProviderImagesFromFacilities>
  );
};

const Auth = () => {
  const { user, hasCompleteProfile, loading: authLoading } = useAuth();
  const { data: providerAccess } = useProviderAccess();
  const { isRecoveryFlow, isPasswordReset } = useUrlParams();
  const { setAsFavorite, favorites, isLoading: favoritesLoading } = useFavorites();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: providers, isLoading: providersLoading } = useProviders();
  const { isDomainLocked, lockedProvider } = useDomainProvider();
  
  // Filter providers when domain is locked
  const displayProviders = isDomainLocked && lockedProvider 
    ? providers?.filter(p => p.id === lockedProvider.id) 
    : providers;
  
  // Get provider ID from URL if coming from a provider page
  const providerIdFromUrl = searchParams.get('provider');
  
  // Store provider ID in localStorage for persistence across tabs/email verification
  useEffect(() => {
    if (providerIdFromUrl) {
      localStorage.setItem('pendingFavoriteProvider', providerIdFromUrl);
    }
  }, [providerIdFromUrl]);

  // Get stored provider ID (either from URL or from previous localStorage)
  const pendingProviderId = providerIdFromUrl || localStorage.getItem('pendingFavoriteProvider');
  
  // Fetch provider data for redirect
  const { data: pendingProvider } = useProvider(pendingProviderId || "");

  // Handle authenticated user redirects - but ONLY if not in recovery flow
  useEffect(() => {
    if (authLoading || favoritesLoading) return; // Wait for auth and favorites to load

    // CRITICAL: Check global recovery flag FIRST - this is set synchronously
    const globalRecoveryFlag = (window as any).__passwordRecoveryActive;
    
    // Multiple recovery detection sources
    const urlParams = new URLSearchParams(window.location.search);
    const urlType = urlParams.get('type');
    const hasAccessToken = urlParams.has('access_token');
    const hasRefreshToken = urlParams.has('refresh_token');
    const hasToken = urlParams.has('token');
    const hasTokenHash = urlParams.has('token_hash');
    const isRecoveryFromStorage = sessionStorage.getItem('isPasswordRecovery') === 'true';
    const urlFragment = window.location.hash;
    
    // ANY of these indicates recovery mode
    const isAnyRecoveryIndicator = globalRecoveryFlag ||
                                   urlType === 'recovery' || 
                                   urlType === 'magiclink' ||
                                   (hasAccessToken && hasRefreshToken) ||
                                   (hasToken && urlType === 'recovery') ||
                                   (hasTokenHash && urlType === 'recovery') ||
                                   urlFragment.includes('type=recovery') ||
                                   (urlFragment.includes('access_token') && urlFragment.includes('recovery')) ||
                                   isRecoveryFromStorage ||
                                   isRecoveryFlow ||
                                   isPasswordReset;
    
    console.log("Auth redirect check:", {
      user: !!user,
      authLoading,
      globalRecoveryFlag,
      urlType,
      isRecoveryFlow,
      isPasswordReset,
      hasAccessToken,
      hasRefreshToken,
      hasToken,
      hasTokenHash,
      isRecoveryFromStorage,
      isAnyRecoveryIndicator,
      pendingProviderId,
      fullUrl: window.location.href
    });
    
    // CRITICAL: Block ALL redirects if ANY recovery indicators are present
    if (isAnyRecoveryIndicator) {
      console.log("BLOCKING REDIRECT - Recovery mode active");
      return;
    }

    // Only proceed with redirects if user is authenticated AND definitely not in recovery
    if (user && !isAnyRecoveryIndicator) {
      console.log("User authenticated and not in recovery, checking for pending favorite...");
      
      // Check if we need to set a favorite provider (for OAuth logins or fallback)
      const storedProviderId = localStorage.getItem('pendingFavoriteProvider');
      if (storedProviderId && favorites.length === 0) {
        console.log("Setting pending favorite provider (OAuth/fallback):", storedProviderId);
        setAsFavorite(storedProviderId);
        localStorage.removeItem('pendingFavoriteProvider');
        
        // Redirect to the provider page after setting favorite
        if (pendingProvider?.slug) {
          navigate(`/provider/${pendingProvider.slug}`, { replace: true });
          return;
        }
      } else {
        // Clear stored provider if user already has favorites
        localStorage.removeItem('pendingFavoriteProvider');
      }
      
      // Standard redirect logic
      if (providerAccess) {
        navigate("/management", { replace: true });
      } else if (hasCompleteProfile) {
        // If user has a favorite, redirect to that provider
        if (favorites.length > 0 && favorites[0]?.providers?.slug) {
          navigate(`/provider/${favorites[0].providers.slug}`, { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } else {
        navigate("/profile-completion", { replace: true });
      }
    }
  }, [user, hasCompleteProfile, providerAccess, authLoading, navigate, isPasswordReset, isRecoveryFlow, favorites, favoritesLoading, setAsFavorite, pendingProvider]);


  return (
    <div className="min-h-screen bg-background">
      {/* Mobile/Tablet: Show login form first */}
      <div className="lg:hidden px-6 pt-8 pb-6">
        <div className="mx-auto w-full max-w-md mb-10">
          <AuthForm />
        </div>

        {/* Only show venues section if not domain locked or if there are providers to show */}
        {(!isDomainLocked || (displayProviders && displayProviders.length > 0)) && (
          <>
            <div className="mb-6">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">
                {isDomainLocked ? 'Welcome to' : 'Available venues'}
              </p>
              <h2 className="text-xl font-semibold text-foreground">
                Book your wellness session
              </h2>
            </div>

            {providersLoading ? (
              <div className="grid grid-cols-1 gap-6">
                {[1, 2].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[4/3] bg-muted rounded-xl mb-3"></div>
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {displayProviders?.map(provider => (
                  <ProviderCard key={provider.id} provider={provider} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="hidden lg:grid lg:grid-cols-2 lg:min-h-screen">
        {/* Left - Auth Form */}
        <div className="flex flex-col justify-center px-6 py-8 lg:py-12 lg:px-12 xl:px-20">
          <div className="mx-auto w-full max-w-md">
            <AuthForm />
          </div>
        </div>

        {/* Right - Provider Cards (Desktop only) */}
        <div className="hidden lg:block bg-muted/30 border-l border-border overflow-y-auto">
          <div className="p-8 xl:p-12">
            {/* Only show venues section if not domain locked or if there are providers to show */}
            {(!isDomainLocked || (displayProviders && displayProviders.length > 0)) && (
              <>
                <div className="mb-6">
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    {isDomainLocked ? 'Welcome to' : 'Available venues'}
                  </p>
                  <h2 className="text-xl font-semibold text-foreground">
                    Book your wellness session
                  </h2>
                </div>

                {providersLoading ? (
                  <div className="grid gap-6">
                    {[1, 2].map(i => (
                      <div key={i} className="animate-pulse">
                        <div className="aspect-[4/3] bg-muted rounded-xl mb-3"></div>
                        <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-muted rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid gap-6">
                    {displayProviders?.map(provider => (
                      <ProviderCard key={provider.id} provider={provider} />
                    ))}
                  </div>
                )}

                {!providersLoading && (!displayProviders || displayProviders.length === 0) && (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">No venues available.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
